import { codeboxApi } from "api/codebox";
import { sentriLockApi } from "api/sentrilock";
import { getHubSpotAuthorizationUrl } from "api/landlord/landlord";
import { useState } from "react";
import {
  BaseInput,
  Button,
  ButtonSize,
  ButtonVariant,
  Show
} from "shared/components";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import { useMutation } from "@tanstack/react-query";
import {
  IntegrationId,
  Step
} from "views/settings-integration/components/AddIntegrationModal/types";
import { useSettingsController } from "views/settings-integration/controller";
import { useAuthContext } from "providers/Auth";

const UsernamePasswordForm = ({
  idPrefix,
  username,
  password,
  onUsernameChange,
  onPasswordChange,
  onBack,
  onSubmit,
  isConnecting
}: {
  idPrefix: string;
  username: string;
  password: string;
  onUsernameChange: (v: string) => void;
  onPasswordChange: (v: string) => void;
  onBack: () => void;
  onSubmit: () => void;
  isConnecting: boolean;
}) => (
  <>
    <div className="space-y-4">
      <BaseInput
        label="Username"
        required
        id={`${idPrefix}-username`}
        placeholder="Username"
        value={username}
        onChange={onUsernameChange}
      />
      <BaseInput
        label="Password"
        required
        id={`${idPrefix}-password`}
        type="password"
        placeholder="**********************"
        value={password}
        onChange={onPasswordChange}
      />
    </div>
    <div className="flex justify-end gap-2 mt-4">
      <Button
        variant={ButtonVariant.BASE}
        size={ButtonSize.BASE}
        onClick={onBack}
        className="w-[125px]"
      >
        Back
      </Button>
      <Button
        variant={ButtonVariant.PRIMARY}
        size={ButtonSize.BASE}
        onClick={onSubmit}
        loading={isConnecting}
        disabled={!username || !password}
        className="w-[125px]"
      >
        Continue
      </Button>
    </div>
  </>
);

interface SecondStepProps {
  setCurrentStep: (step: Step) => void;
  closeModal: () => void;
  selectedIntegration: string;
  onConnectSuccess?: (integrationId: IntegrationId) => void;
}

export const SecondStep = ({
  setCurrentStep,
  closeModal,
  selectedIntegration,
  onConnectSuccess
}: SecondStepProps) => {
  const { updateSettings, isUpdateSettingsPending } = useSettingsController();
  const { refreshPartnerData } = useAuthContext();

  const [googleAnalyticsId, setGoogleAnalyticsId] = useState("");
  const [codeboxUsername, setCodeboxUsername] = useState("");
  const [codeboxPassword, setCodeboxPassword] = useState("");
  const [sentriLockUsername, setSentriLockUsername] = useState("");
  const [sentriLockPassword, setSentriLockPassword] = useState("");

  const { mutate: connectCodebox, isPending: isConnectingCodebox } =
    useMutation({
      mutationKey: ["CODEBOX", "CREATE"],
      mutationFn: () =>
        codeboxApi.connect({
          username: codeboxUsername,
          password: codeboxPassword
        }),
      onSuccess: () => {
        refreshPartnerData();
        closeModal();
        onConnectSuccess?.(IntegrationId.CODEBOX);
      },
      onError: (err) => {
        toast.error(getErrorMessage(err));
      }
    });

  const { mutate: connectSentriLock, isPending: isConnectingSentriLock } =
    useMutation({
      mutationKey: ["SENTRILOCK", "CREATE"],
      mutationFn: () =>
        sentriLockApi.connect({
          username: sentriLockUsername,
          password: sentriLockPassword
        }),
      onSuccess: () => {
        refreshPartnerData();
        closeModal();
      },
      onError: (err) => {
        toast.error(getErrorMessage(err));
      }
    });

  const handleAuthorizeHubSpot = async () => {
    const data = await getHubSpotAuthorizationUrl();
    window.open(data.authorization_url, "HubSpot Auth");
  };

  return (
    <>
      <Show if={selectedIntegration === IntegrationId.GA}>
        <BaseInput
          label="GA account ID"
          required
          id="ga-account-id"
          placeholder="G-XYZ"
          value={googleAnalyticsId}
          onChange={setGoogleAnalyticsId}
        />
        <div className="flex justify-end gap-2 mt-4">
          <Button
            variant={ButtonVariant.BASE}
            size={ButtonSize.BASE}
            onClick={() => setCurrentStep(Step.FIRST)}
          >
            Cancel
          </Button>
          <Button
            loading={isUpdateSettingsPending}
            variant={ButtonVariant.PRIMARY}
            size={ButtonSize.BASE}
            onClick={() => {
              updateSettings({ googleAnalyticsId }).then(() => closeModal());
            }}
            disabled={!googleAnalyticsId}
          >
            Continue
          </Button>
        </div>
      </Show>

      <Show if={selectedIntegration === IntegrationId.HUBSPOT}>
        <Button
          variant={ButtonVariant.PRIMARY}
          size={ButtonSize.BASE}
          onClick={handleAuthorizeHubSpot}
        >
          Connect to HubSpot
        </Button>
      </Show>

      <Show if={selectedIntegration === IntegrationId.CODEBOX}>
        <UsernamePasswordForm
          idPrefix="codebox"
          username={codeboxUsername}
          password={codeboxPassword}
          onUsernameChange={setCodeboxUsername}
          onPasswordChange={setCodeboxPassword}
          onBack={() => setCurrentStep(Step.FIRST)}
          onSubmit={() => connectCodebox()}
          isConnecting={isConnectingCodebox}
        />
      </Show>

      <Show if={selectedIntegration === IntegrationId.SENTRILOCK}>
        <UsernamePasswordForm
          idPrefix="sentrilock"
          username={sentriLockUsername}
          password={sentriLockPassword}
          onUsernameChange={setSentriLockUsername}
          onPasswordChange={setSentriLockPassword}
          onBack={() => setCurrentStep(Step.FIRST)}
          onSubmit={() => connectSentriLock()}
          isConnecting={isConnectingSentriLock}
        />
      </Show>
    </>
  );
};
